/**
 * Read-only view of an Agreement Interaction — open to ANY user who can see
 * the row (polish 2026-06-29). Previously the full body was reachable only
 * through the author-gated Edit modal, so a non-author could read only the
 * 2-line list preview, never the full interaction report. The list endpoint
 * already returns the full body and is visibility-scoped server-side (private
 * notes by others are never in the list), so this is a pure read affordance;
 * Edit stays author-only.
 */
import { Badge, Divider, Group, Modal, Stack, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';

import type { AgreementInteraction } from '../api/types';
import { INTERACTION_KIND_LABELS } from '../api/types';

interface Props {
  opened: boolean;
  onClose: () => void;
  interaction: AgreementInteraction | null;
}

function fmtDate(iso?: string | null): string {
  return iso ? iso.substring(0, 10) : '—';
}
function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  return iso.substring(0, 10) + ' ' + iso.substring(11, 16);
}

export function InteractionViewModal({ opened, onClose, interaction }: Props) {
  const it = interaction;
  return (
    <Modal opened={opened} onClose={onClose}
      title={it?.title || 'Interaction'} size="lg" centered>
      {it && (
        <Stack gap="sm">
          <Group gap="xs">
            <Badge variant="default" size="sm">
              {INTERACTION_KIND_LABELS[it.kind] ?? it.kind}
            </Badge>
            <Text size="sm" c="dimmed" ff="monospace">{fmtDate(it.interaction_date)}</Text>
            {it.is_private && (
              <Badge color="gray" variant="light" size="sm" leftSection={<IconLock size={11} />}>
                Private
              </Badge>
            )}
          </Group>

          <div>
            <Text size="xs" c="dimmed" fw={500}>Body</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {it.body?.trim() ? it.body : '—'}
            </Text>
          </div>

          {(it.person_ids.length > 0 || it.user_ids.length > 0 || it.has_other_attendees) && (
            <div>
              <Text size="xs" c="dimmed" fw={500}>Attendees</Text>
              <Group gap={6} wrap="wrap" mt={2}>
                {it.person_ids.map((pid) => (
                  <Badge key={`p-${pid}`} variant="light" color="blue" size="sm">
                    {it.person_labels[pid] ?? pid.substring(0, 8)}
                  </Badge>
                ))}
                {it.user_ids.map((uid) => (
                  <Badge key={`u-${uid}`} variant="light" color="grape" size="sm">
                    {it.user_labels[uid] ?? uid.substring(0, 8)}
                  </Badge>
                ))}
                {it.has_other_attendees && (
                  <Badge variant="outline" color="gray" size="sm"
                    styles={{ root: { fontStyle: 'italic' } }}>
                    + Other (described in body)
                  </Badge>
                )}
              </Group>
            </div>
          )}

          <Divider />
          <Text size="xs" c="dimmed">
            Filed by {it.created_by_label ?? '—'} · {fmtDateTime(it.created_at)}
          </Text>
        </Stack>
      )}
    </Modal>
  );
}
